import React from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardActionArea,
  SxProps,
  Theme,
} from "@mui/material";
import { useStoreDetails } from "../common/Providers/StoreDetailsProvider/UseStoreDetails";

interface SelectCardProps {
  label: string;
  subtitle?: string;
  selected?: boolean;
  onClick?: () => void;
  color?: string;
  centerText?: boolean;
  /** Outer card style overrides */
  sx?: SxProps<Theme>;
  /** CardActionArea inner style overrides */
  contentSx?: SxProps<Theme>;
  /** Style overrides for the label text */
  labelSx?: SxProps<Theme>;
  /** Style overrides for the subtitle text */
  subtitleSx?: SxProps<Theme>;
}

interface SelectImageCardProps extends SelectCardProps {
  imageUrl?: string;
  imageAlt?: string;
  labelHtml?: string;
}

interface OptionItem {
  id: string;
  label: string;
  labelHtml?: string;
  subtitle?: string;
  imageUrl?: string;
  imageAlt?: string;
}

interface SelectBoxGridProps {
  title?: string;
  options: OptionItem[];
  selectedId: string;
  onSelect: (id: string) => void;
  columns?: { xs?: number; sm?: number; md?: number; lg?: number };
  useImage?: boolean;
  /** Global overrides for cards in this grid */
  cardContainerSx?: SxProps<Theme>;
  cardSx?: SxProps<Theme>;
  cardContentSx?: SxProps<Theme>;
  labelSx?: SxProps<Theme>;
  subtitleSx?: SxProps<Theme>;
}

// ================= SelectImageCard =================
const SelectImageCard: React.FC<SelectImageCardProps> = ({
  label,
  labelHtml,
  subtitle,
  selected = false,
  onClick,
  color = "#0047c0",
  centerText = false,
  imageUrl,
  imageAlt = "",
  sx,
  contentSx,
  labelSx,
  subtitleSx,
}) => (
  <Card
    onClick={onClick}
    sx={{
      cursor: "pointer",
      borderRadius: 2,
      // border: "1px solid",
      // borderColor: "rgba(0,0,0,0.08)",
      // backgroundColor: "white",
      boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
      transition: "all 0.25s ease",
      height: "100%",
      border: selected ? "2px solid" : "1px solid",
      borderColor: selected ? color : "rgba(0,0,0,0.08)",
      backgroundColor: selected ? color : "white",
      "&:hover": {
        boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
        transform: "translateY(-2px)",
      },
      ...sx,
    }}
  >
    <CardActionArea
      sx={{
        p: 0,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
        ...contentSx,
      }}
    >
      <Box
        sx={{
          width: "100%",
          paddingTop: "66.67%",
          position: "relative",
          backgroundColor: "#f5f5f5",
          overflow: "hidden"
        }}
      >
        {imageUrl && (
          <Box
            component="img"
            src={imageUrl}
            alt={imageAlt}
            title={imageAlt}
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              objectFit: "cover"
            }}
          />
        )}
      </Box>

      <Box sx={{ p: 2.5, textAlign: centerText ? "center" : "left" }}>
        {labelHtml ? (
          <div
            className="ql-editor"
            style={{ padding: 0, fontWeight: 700, fontSize: '1.125rem', color: selected ? 'white' : undefined }}
            dangerouslySetInnerHTML={{ __html: labelHtml }}
          />
        ) : (
          <Typography
            variant="h6"
            fontWeight={700}
            color={selected ? "white" : "text.primary"}
            sx={{ fontSize: "1.125rem", ...labelSx }}
          >
            {label}
          </Typography>
        )}

        {subtitle && (
          <Typography
            variant="body2"
            color={selected ? "white" : "text.secondary"}
            sx={{ mt: 0.5, ...subtitleSx }}
          >
            {subtitle}
          </Typography>
        )}
      </Box>
    </CardActionArea>
  </Card>
);

// ================= SelectCard =================
export const SelectCard: React.FC<SelectCardProps> = ({
  label,
  subtitle,
  selected = false,
  onClick,
  color = "#0047c0",
  centerText = true,
  sx,
  contentSx,
  labelSx,
  subtitleSx,
}) => (
  <Card
    onClick={onClick}
    sx={{
      cursor: "pointer",
      borderRadius: 1,
      border: selected ? "2px solid" : "1px solid",
      borderColor: selected ? color : "divider",
      backgroundColor: selected ? color : "background.paper",
      transition: "all 0.25s ease",
      "&:hover": { borderColor: color },
      ...sx,
    }}
  >
    <CardActionArea
      sx={{
        p: 2,
        textAlign: centerText ? "center" : "left",
        ...contentSx,
      }}
    >
      <Typography
        variant="body1"
        fontWeight={selected ? 600 : 500}
        color={selected ? "white" : "text.primary"}
        sx={labelSx}
      >
        {label}
      </Typography>

      {subtitle && (
        <Typography
          variant="body2"
          color={selected ? "white" : "text.secondary"}
          sx={{ mt: 0.5, ...subtitleSx }}
        >
          {subtitle}
        </Typography>
      )}
    </CardActionArea>
  </Card>
);

// ================= SelectBoxGrid =================
const SelectBoxGrid: React.FC<SelectBoxGridProps> = ({
  title,
  options,
  selectedId,
  onSelect,
  columns = { xs: 12, sm: 6, md: 4, lg: 3 },
  useImage = false,
  cardContainerSx,
  cardSx,
  cardContentSx,
  labelSx,
  subtitleSx,
}) => { 
  const { storeContent } = useStoreDetails();
  
  return (
    <Box sx={{ p: 1.5, mb: 2, ...cardContainerSx }}>
      {title && (
        <Typography fontWeight={600} mb={1}>
          {title}
        </Typography>
      )}
      <Grid container spacing={2}>
        {options.map((opt) => (
          <Grid item key={opt.id} {...columns}>
            {useImage && (
              <SelectImageCard
                color={storeContent.primary_color || '#0047C0'}
                label={opt.label}
                labelHtml={opt.labelHtml}
                subtitle={opt.subtitle}
                imageUrl={opt.imageUrl}
                imageAlt={opt.imageAlt}
                selected={selectedId === opt.id}
                onClick={() => onSelect(opt.id)}
                sx={cardSx}
                contentSx={cardContentSx}
                labelSx={labelSx}
                subtitleSx={subtitleSx}
              />
            )}

            {!useImage && (
              <SelectCard
                color={storeContent.primary_color || '#0047C0'}
                label={opt.label}
                subtitle={opt.subtitle}
                selected={selectedId === opt.id}
                onClick={() => onSelect(opt.id)}
                sx={cardSx}
                contentSx={cardContentSx}
                labelSx={labelSx}
                subtitleSx={subtitleSx}
              />
            )}
          </Grid>
        ))}
      </Grid>
    </Box>
)};

export default SelectBoxGrid;
